import React, { useState, useEffect } from 'react';
import { Clock, FileText, ChevronRight, Trash2 } from 'lucide-react';

/**
 * ReportHistory Component (API-based)
 * Displays last 10 generated bank reports for quick access
 * 
 * Features:
 * - Shows recent reports with metadata
 * - Click to load previous report
 * - Delete reports
 * - Timestamp and slide count
 * 
 * Uses API endpoints instead of direct Supabase access
 */
const ReportHistory = ({ userId, onLoadReport, onClose }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (userId) {
      loadReportHistory();
    }
  }, [userId]);

  const loadReportHistory = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/reports/bank/history?userId=${userId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Laden der Historie');
      }

      const data = await response.json();
      setReports(data.reports || []);
      console.log('[HISTORY] Loaded', data.count || 0, 'reports');
    } catch (err) {
      console.error('[HISTORY] Load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReport = async (reportId, reportName, event) => {
    event.stopPropagation(); // Prevent triggering load

    if (!confirm(`Report "${reportName}" wirklich löschen?`)) {
      return;
    }

    try {
      setDeleting(reportId);

      const response = await fetch(`/api/reports/bank/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Löschen');
      }

      const data = await response.json();
      console.log('[HISTORY] Deleted:', data.deletedReport);

      // Remove from list
      setReports(reports.filter(r => r.id !== reportId));
    } catch (err) {
      console.error('[HISTORY] Delete error:', err);
      alert('Fehler beim Löschen: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  const handleLoadReport = async (reportId) => {
    // For now, just close the history modal
    // In a full implementation, this would fetch the full report data
    // and pass it to onLoadReport callback
    console.log('[HISTORY] Load report:', reportId);
    
    if (onLoadReport) {
      // This would need to fetch the full report data
      // For now, we just close the modal
      alert('Report-Laden noch nicht implementiert. Report ID: ' + reportId);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl shadow-2xl border border-white/10 w-full max-w-3xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-400" />
            <h2 className="text-xl font-bold text-white">Report-Historie</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-gray-400">
                <Clock className="w-5 h-5 animate-spin" />
                <span>Lade Historie...</span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
              <p className="font-semibold">Fehler beim Laden</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {!loading && !error && reports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">Noch keine Reports vorhanden</p>
              <p className="text-sm text-gray-500 mt-1">Generiere deinen ersten Bank Report</p>
            </div>
          )}

          {!loading && !error && reports.length > 0 && (
            <div className="space-y-3">
              {reports.map((report) => (
                <div
                  key={report.id}
                  className="group relative flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-blue-500/30 transition-all"
                >
                  <button
                    onClick={() => handleLoadReport(report.id)}
                    className="flex-1 flex items-center gap-4 text-left"
                  >
                    <FileText className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-base text-white font-medium truncate">
                        {report.property_name || 'Unbenannter Report'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                        <span>{formatDate(report.created_at)}</span>
                        <span>•</span>
                        <span>{report.slides_count || 0} Slides</span>
                        {report.recipient_email && (
                          <>
                            <span>•</span>
                            <span className="text-green-400">✉ Versendet</span>
                          </>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={(e) => handleDeleteReport(report.id, report.property_name, e)}
                    disabled={deleting === report.id}
                    className="ml-3 p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 hover:border-red-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    title="Report löschen"
                  >
                    {deleting === report.id ? (
                      <Clock className="w-4 h-4 text-red-400 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-red-400" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && reports.length > 0 && (
          <div className="px-6 py-4 border-t border-white/10 bg-white/5">
            <p className="text-sm text-gray-400 text-center">
              {reports.length} {reports.length === 1 ? 'Report' : 'Reports'} gespeichert
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportHistory;

